import { useState } from "react";
import { useTrackingProducts, useDeleteProduct } from "@/hooks/useTrackingProducts";
import { useConsoleDevices } from "@/hooks/useConsoleDevices";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw, Cloud } from "lucide-react";
import { CreateProductModal } from "./CreateProductModal";
import { ProductSimpleList } from "./ProductSimpleList";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface AdminProductsContentProps {
  productType: 'gps_device';
}

export function AdminProductsContent({ productType }: AdminProductsContentProps) {
  const { data: products = [], isLoading } = useTrackingProducts(productType);
  const deleteMutation = useDeleteProduct();
  const { loading: isSyncing, progress, isConfigured, syncSingleDevice, syncAllDevices } = useConsoleDevices();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  
  const title = "Dispositivos GPS";
  const description = "Gerencie os dispositivos GPS disponíveis para venda";

  const syncedCount = products.filter(p => p.console_device_id).length;
  const unsyncedCount = products.length - syncedCount;

  const handleSyncAll = async () => {
    if (products.length === 0) return;
    await syncAllDevices(products);
  };

  const handleSyncSingle = async (product: any) => {
    await syncSingleDevice(product);
  };

  if (isLoading) {
    return <div>Carregando produtos...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Status de sincronização */}
          {products.length > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2">
                    <Cloud className="h-4 w-4 text-muted-foreground" />
                    <Badge variant="outline" className="font-normal">
                      {syncedCount}/{products.length} sincronizados
                    </Badge>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {unsyncedCount > 0 
                    ? `${unsyncedCount} dispositivo(s) pendente(s) de sincronização`
                    : 'Todos os dispositivos estão sincronizados'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Botão Sincronizar Todos */}
          {isConfigured && products.length > 0 && (
            <Button 
              variant="outline" 
              onClick={handleSyncAll}
              disabled={isSyncing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
              {isSyncing 
                ? `Sincronizando ${progress.current}/${progress.total}...` 
                : 'Sincronizar Todos'}
            </Button>
          )}

          <Button onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Produto
          </Button>
        </div>
      </div>

      <ProductSimpleList
        products={products}
        productType={productType}
        onEdit={(product) => {
          setEditingProduct(product);
          setCreateModalOpen(true);
        }}
        onDelete={(product) => deleteMutation.mutate(product.id)}
        onSync={isConfigured ? handleSyncSingle : undefined}
        isSyncing={isSyncing}
      />

      <CreateProductModal
        open={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setEditingProduct(null);
        }}
        productType={productType}
        editingProduct={editingProduct}
      />
    </div>
  );
}
